import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

export const useRedirectUrl = () => {
  const location = useLocation()

  const redirectUrl = encodeURIComponent(`${location.pathname}${location.search}${location.hash}`)

  const { redirectUrlSearchParam, redirectUrlSearchParamEncoded, redirectPath } = useMemo(() => {
    const searchParams = new URLSearchParams(location.search)
    const redirectUrlSearchParam = searchParams.get('redirectUrl')
    const redirectUrlSearchParamEncoded = redirectUrlSearchParam
      ? encodeURIComponent(redirectUrlSearchParam)
      : undefined

    const redirectPath = redirectUrlSearchParam
      ? buildRedirectPathWithMergedParams(redirectUrlSearchParam, location.search)
      : null

    return {
      redirectUrlSearchParam,
      redirectUrlSearchParamEncoded,
      redirectPath,
    }
  }, [location.search])

  return {
    redirectUrl,
    redirectUrlSearchParam,
    redirectUrlSearchParamEncoded,
    redirectPath,
  }
}

/**
 * Parses a redirect URL to extract the pathname and existing query parameters.
 * Handles both full URLs and relative paths.
 */
function parseRedirectUrl(redirectUrl: string): {
  pathname: string
  searchParams: URLSearchParams
} {
  try {
    // Try parsing as full URL first
    const redirectUrlObj = new URL(redirectUrl, window.location.origin)
    return {
      pathname: redirectUrlObj.pathname,
      searchParams: new URLSearchParams(redirectUrlObj.search),
    }
  } catch {
    // If that fails, parse as relative path
    const [pathname, search] = redirectUrl.split('?')
    return {
      pathname: pathname || redirectUrl,
      searchParams: new URLSearchParams(search || ''),
    }
  }
}

/**
 * Merges query parameters from the current URL into the redirect URL's query parameters.
 * Current URL params take precedence over redirect URL params.
 */
function mergeQueryParams(
  redirectSearchParams: URLSearchParams,
  currentSearch: string
): URLSearchParams {
  const currentSearchParams = new URLSearchParams(currentSearch)
  currentSearchParams.delete('redirectUrl')

  // Merge query params: current URL params take precedence
  currentSearchParams.forEach((value, key) => {
    redirectSearchParams.set(key, value)
  })

  return redirectSearchParams
}

/**
 * Builds the final redirect path with merged query parameters.
 */
function buildRedirectPath(pathname: string, searchParams: URLSearchParams): string {
  const queryString = searchParams.toString()
  return queryString ? `${pathname}?${queryString}` : pathname
}

/**
 * Builds a redirect path from a redirectUrl, merging in query params from the current URL.
 */
function buildRedirectPathWithMergedParams(redirectUrl: string, currentSearch: string): string {
  const { pathname, searchParams } = parseRedirectUrl(redirectUrl)
  const mergedParams = mergeQueryParams(searchParams, currentSearch)
  return buildRedirectPath(pathname, mergedParams)
}
